import { formatNumber, formatCurrency, formatPercentage } from '@tracekit/core';

interface DashboardMetrics {
  [key: string]: any;
}

interface ExportOptions {
  format: 'json' | 'csv' | 'excel';
  filename?: string;
  includeTimestamp?: boolean;
}

/**
 * Export dashboard metrics to JSON format
 */
export function exportToJSON(metrics: DashboardMetrics, options: ExportOptions = { format: 'json' }): void {
  const timestamp = new Date().toISOString();
  const data = {
    exportedAt: timestamp,
    metrics,
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.filename || `dashboard-export-${timestamp.split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export dashboard metrics to CSV format
 */
export function exportToCSV(metrics: DashboardMetrics, options: ExportOptions = { format: 'csv' }): void {
  const timestamp = new Date().toISOString();
  
  // Convert metrics to CSV rows
  const rows: string[][] = [
    ['Metric', 'Value', 'Category'],
  ];

  // Pipeline Metrics
  rows.push(['Total Opportunities', formatNumber(metrics.totalOpportunities), 'Pipeline']);
  rows.push(['Active Opportunities', formatNumber(metrics.activeOpportunities), 'Pipeline']);
  rows.push(['Closed Opportunities', formatNumber(metrics.closedOpportunities), 'Pipeline']);
  rows.push(['Pipeline Value', formatCurrency(metrics.pipelineValue), 'Pipeline']);
  rows.push(['Avg Contract Value', formatCurrency(metrics.avgContractValue), 'Pipeline']);
  rows.push(['Avg Win Probability', formatPercentage(metrics.avgWinProbability), 'Pipeline']);

  // Proposal Metrics
  rows.push(['Total Proposals', formatNumber(metrics.totalProposals), 'Proposals']);
  rows.push(['Won Proposals', formatNumber(metrics.wonProposals), 'Proposals']);
  rows.push(['Lost Proposals', formatNumber(metrics.lostProposals), 'Proposals']);
  rows.push(['Pending Proposals', formatNumber(metrics.pendingProposals), 'Proposals']);
  rows.push(['Win Rate', formatPercentage(metrics.winRate), 'Proposals']);
  rows.push(['Revenue YTD', formatCurrency(metrics.revenueYTD), 'Proposals']);

  // Client Metrics
  rows.push(['Total Clients', formatNumber(metrics.totalClients), 'Clients']);
  rows.push(['Active Clients', formatNumber(metrics.activeClients), 'Clients']);
  rows.push(['Repeat Clients', formatNumber(metrics.repeatClients), 'Clients']);
  rows.push(['Avg Client Satisfaction', formatPercentage(metrics.avgClientSatisfaction), 'Clients']);

  // Staff Metrics
  rows.push(['Total Staff', formatNumber(metrics.totalStaff), 'Resources']);
  rows.push(['Available Staff', formatNumber(metrics.availableStaff), 'Resources']);
  rows.push(['Fully Booked Staff', formatNumber(metrics.fullyBookedStaff), 'Resources']);
  rows.push(['Utilization Rate', formatPercentage(metrics.utilizationRate), 'Resources']);
  rows.push(['Avg Hourly Rate', formatCurrency(metrics.avgHourlyRate), 'Resources']);

  // Project Execution
  rows.push(['Active Workplans', formatNumber(metrics.activeWorkPlans), 'Execution']);
  rows.push(['Completed Workplans', formatNumber(metrics.completedWorkPlans), 'Execution']);
  rows.push(['Workplans Needing Renewal', formatNumber(metrics.workplansNeedingRenewal), 'Execution']);
  rows.push(['Avg Workplan Duration', `${metrics.avgWorkplanDuration.toFixed(1)} weeks`, 'Execution']);

  // Capability
  rows.push(['Total Past Projects', formatNumber(metrics.totalPastProjects), 'Capability']);
  rows.push(['Completed Projects', formatNumber(metrics.completedProjects), 'Capability']);
  rows.push(['Ongoing Projects', formatNumber(metrics.ongoingProjects), 'Capability']);
  rows.push(['Avg Project Value', formatCurrency(metrics.avgProjectValue), 'Capability']);

  // Qualifications
  rows.push(['Total Qualifications', formatNumber(metrics.totalQualifications), 'Qualifications']);
  rows.push(['Active Qualifications', formatNumber(metrics.activeQualifications), 'Qualifications']);
  rows.push(['Expiring Qualifications', formatNumber(metrics.expiringQualifications), 'Qualifications']);
  rows.push(['Expired Qualifications', formatNumber(metrics.expiredQualifications), 'Qualifications']);

  // Compliance
  rows.push(['Total Requirements', formatNumber(metrics.totalRequirements), 'Compliance']);
  rows.push(['Compliant Requirements', formatNumber(metrics.compliantRequirements), 'Compliance']);
  rows.push(['Non-Compliant Requirements', formatNumber(metrics.nonCompliantRequirements), 'Compliance']);
  rows.push(['High Risk Requirements', formatNumber(metrics.highRiskRequirements), 'Compliance']);
  rows.push(['Compliance Rate', formatPercentage(metrics.complianceRate), 'Compliance']);

  // Technical
  rows.push(['Total Approaches', formatNumber(metrics.totalApproaches), 'Technical']);
  rows.push(['Total SOPs', formatNumber(metrics.totalSOPs), 'Technical']);
  rows.push(['Avg Success Rate', formatPercentage(metrics.avgSuccessRate), 'Technical']);

  // Deadlines
  rows.push(['Deadlines Next 7 Days', formatNumber(metrics.deadlinesNext7Days), 'Deadlines']);
  rows.push(['Deadlines Next 30 Days', formatNumber(metrics.deadlinesNext30Days), 'Deadlines']);

  // Additional Entities
  rows.push(['RFP Documents', formatNumber(metrics.totalRFPDocuments), 'Documents']);
  rows.push(['Bid Documents', formatNumber(metrics.totalBidDocuments), 'Documents']);
  rows.push(['Submissions', formatNumber(metrics.totalSubmissions), 'Documents']);
  rows.push(['Capability Statements', formatNumber(metrics.totalCapabilityStatements), 'Documents']);
  rows.push(['Opportunity Match Scores', formatNumber(metrics.totalOpportunityMatchScores), 'Analysis']);
  rows.push(['RFP Requirements', formatNumber(metrics.totalRFPRequirements), 'Requirements']);
  rows.push(['Past Performance Examples', formatNumber(metrics.totalPastPerformanceExamples), 'Evidence']);
  rows.push(['Team Compositions', formatNumber(metrics.totalTeamCompositions), 'Teams']);
  rows.push(['Budget Estimates', formatNumber(metrics.totalBudgetEstimates), 'Financial']);
  rows.push(['Subcontractors', formatNumber(metrics.totalSubcontractors), 'Partners']);
  rows.push(['Grants', formatNumber(metrics.totalGrants), 'Opportunities']);
  rows.push(['Contract Awards', formatNumber(metrics.totalContractAwards), 'Opportunities']);

  // Convert to CSV string
  const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.filename || `dashboard-export-${timestamp.split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export dashboard metrics to Excel format (using CSV with .xlsx extension for simplicity)
 */
export function exportToExcel(metrics: DashboardMetrics, options: ExportOptions = { format: 'excel' }): void {
  // For now, use CSV format but with .xlsx extension
  // In a production environment, you'd use a library like xlsx or exceljs
  const timestamp = new Date().toISOString();
  
  const rows: string[][] = [
    ['Executive Dashboard Export'],
    ['Generated:', timestamp],
    [''],
    ['Metric', 'Value', 'Category'],
  ];

  // Add all metrics (same as CSV)
  rows.push(['Total Opportunities', formatNumber(metrics.totalOpportunities), 'Pipeline']);
  rows.push(['Active Opportunities', formatNumber(metrics.activeOpportunities), 'Pipeline']);
  rows.push(['Pipeline Value', formatCurrency(metrics.pipelineValue), 'Pipeline']);
  rows.push(['Win Rate', formatPercentage(metrics.winRate), 'Proposals']);
  rows.push(['Revenue YTD', formatCurrency(metrics.revenueYTD), 'Financial']);
  // ... (add all other metrics as needed)

  const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.filename || `dashboard-export-${timestamp.split('T')[0]}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Main export function that routes to the appropriate format
 */
export function exportDashboard(metrics: DashboardMetrics, options: ExportOptions): void {
  switch (options.format) {
    case 'json':
      exportToJSON(metrics, options);
      break;
    case 'csv':
      exportToCSV(metrics, options);
      break;
    case 'excel':
      exportToExcel(metrics, options);
      break;
    default:
      console.error('Unsupported export format:', options.format);
  }
}

